import * as klinecharts from "klinecharts";

export interface TextExtendData {
  text: string;
  variant: "box" | "tag";
  size: "S" | "M" | "L" | "XL";   // S=12, M=14, L=18, XL=24
  color: string;
  bold: boolean;
  italic: boolean;
  showBackground: boolean;
  background: string;
}

function getFontSizePx(size: string): number {
  switch (size) {
    case "S": return 12;
    case "M": return 14;
    case "L": return 18;
    case "XL": return 24;
    default: return 14;
  }
}

let textMeasureContext: CanvasRenderingContext2D | null = null;

function measureTextWidth(text: string, font: string): number {
  if (typeof document === "undefined") {
    return text.length * 8; // fallback
  }
  if (!textMeasureContext) {
    const canvas = document.createElement("canvas");
    textMeasureContext = canvas.getContext("2d");
  }
  if (textMeasureContext) {
    textMeasureContext.font = font;
    return Math.ceil(textMeasureContext.measureText(text).width);
  }
  return text.length * 8;
}

let textRegistered = false;

export async function registerTextOverlay() {
  if (textRegistered) return;
  textRegistered = true;

  try {
    klinecharts.registerOverlay({
      name: "text",
      totalStep: 2,
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({ overlay, coordinates }: any) => {
        if (!coordinates || coordinates.length === 0) return [];
        const anchor = coordinates[0];
        const x = anchor.x;
        const y = anchor.y;

        const data: any = overlay.extendData || {};
        const textStr = data.text !== undefined ? data.text : "";
        const variant = data.variant || "box";
        const sizeStr = data.size || "M";
        const fontSize = getFontSizePx(sizeStr);
        const color = data.color || "#E4EAF4";
        const bold = !!data.bold;
        const italic = !!data.italic;
        const showBackground = data.showBackground !== false;
        const background = data.background || (variant === "tag" ? "rgba(148, 163, 184, 0.25)" : "rgba(30, 41, 59, 0.5)");

        const font = `${italic ? "italic " : ""}${bold ? "bold " : ""}${fontSize}px IBM Plex Sans, Inter, Roboto, sans-serif`;

        // If the text is completely empty (placing/before typing), show a placeholder
        if (!textStr) {
          const placeholder = variant === "tag" ? "Tag..." : "Type text...";
          const width = measureTextWidth(placeholder, font) + 16;
          const height = fontSize + 12;

          return [
            // placeholder rect background
            {
              type: "rect",
              attrs: {
                x: x,
                y: y - height / 2,
                width,
                height,
              },
              styles: {
                style: "stroke_fill",
                color: "rgba(255, 255, 255, 0.05)",
                borderColor: "rgba(255, 255, 255, 0.2)",
                borderSize: 1,
                borderStyle: "dashed",
                borderRadius: 4,
              },
            },
            // placeholder text
            {
              type: "text",
              attrs: {
                x: x + 8,
                y: y,
                text: placeholder,
                align: "left",
                baseline: "middle",
              },
              styles: {
                color: "rgba(255, 255, 255, 0.4)",
                size: fontSize,
                family: "IBM Plex Sans, Inter, Roboto, sans-serif",
                weight: bold ? "bold" : "normal",
              },
            },
          ];
        }

        if (variant === "tag") {
          // TAG VARIANT: Single line centered tag pill
          const lines = [textStr.replace(/\n/g, " ")]; // tags are single-line
          const textW = measureTextWidth(lines[0], font);
          const px = 12; // horizontal padding
          const py = 6;  // vertical padding
          const width = textW + px * 2;
          const height = fontSize + py * 2;

          const rectX = x - width / 2;
          const rectY = y - height / 2;

          const figures: any[] = [];

          if (showBackground) {
            figures.push({
              type: "rect",
              attrs: {
                x: rectX,
                y: rectY,
                width,
                height,
              },
              styles: {
                style: "stroke_fill",
                color: background,
                borderColor: background,
                borderSize: 1,
                borderStyle: "solid",
                borderRadius: 6, // rounded pill corners
              },
            });
          }

          figures.push({
            type: "text",
            attrs: {
              x: x,
              y: y,
              text: lines[0],
              align: "center",
              baseline: "middle",
            },
            styles: {
              color: color,
              size: fontSize,
              family: "IBM Plex Sans, Inter, Roboto, sans-serif",
              weight: bold ? "bold" : "normal",
            },
          });

          return figures;
        } else {
          // BOX VARIANT: Multi-line text box
          const lines = textStr.split("\n");
          let maxW = 0;
          for (const line of lines) {
            const w = measureTextWidth(line, font);
            if (w > maxW) maxW = w;
          }

          const px = 12; // padding left/right
          const py = 10; // padding top/bottom
          const lineHeight = Math.max(fontSize + 4, 16);
          const width = maxW + px * 2;
          const height = lines.length * lineHeight + py * 2;

          // Box anchor is top-left
          const rectX = x;
          const rectY = y;

          const figures: any[] = [];

          if (showBackground) {
            figures.push({
              type: "rect",
              attrs: {
                x: rectX,
                y: rectY,
                width,
                height,
              },
              styles: {
                style: "stroke_fill",
                color: background,
                borderColor: "rgba(255, 255, 255, 0.1)",
                borderSize: 1,
                borderStyle: "solid",
                borderRadius: 4,
              },
            });
          }

          // Render each line
          lines.forEach((line, index) => {
            const lineY = rectY + py + index * lineHeight + lineHeight / 2;
            figures.push({
              type: "text",
              attrs: {
                x: rectX + px,
                y: lineY,
                text: line,
                align: "left",
                baseline: "middle",
              },
              styles: {
                color: color,
                size: fontSize,
                family: "IBM Plex Sans, Inter, Roboto, sans-serif",
                weight: bold ? "bold" : "normal",
              },
            });
          });

          return figures;
        }
      },
    });
  } catch (err) {
    console.error("Failed to register text overlay:", err);
  }
}
